/**
 * FE-081. What the validation screens tell the person at the vehicle after "Show message on
 * rooftop display".
 *
 * These screens used to render one sentence — "The taxi roof light is offline" — for three
 * different causes: any API error, a light that really was offline, and a light the backend had
 * just reported ONLINE that did not confirm showing the message. The last is the common one, and
 * the sentence asserted the one thing the code had just ruled out.
 *
 * Each cause now gets its own sentence, and a sentence may only claim what the response states.
 */

/**
 * BE-143. The status the app reports when the USER removes a payload from its queue, decided by
 * Cristian on 2026-09-22. The backend parses it exactly and case-sensitively — `CANCELED` would
 * be dropped — and pins it with `CancelledStatusContractBe143Test`. Pinned here by
 * validationOutcome.test.ts, so a rename on either side fails a build.
 *
 * Not `ADMIN_CANCELLED`: a separate value nothing produces, whose name means an admin did it.
 */
export const PAYLOAD_STATUS_CANCELLED = 'CANCELLED'

export const DEVICE_RESPONSE_SHOWING = 'SHOWING'

export type ValidationOutcome = {ok: boolean; text: string}

export const describeValidationOutcome = (response: any): ValidationOutcome => {
  if (response?.Status != 200) {
    // The request failed. Nothing is known about the light, so nothing is said about it.
    return {ok: false, text: 'The validation could not be completed. Please try again.'}
  }

  const result = response.Response

  if (result?.Valid === false) {
    return {ok: false, text: 'The vehicle could not be found.'}
  }

  if (result?.DeviceOnline === false) {
    return {ok: false, text: 'The taxi roof light is offline, validation message cannot be shown.'}
  }

  if (
    result?.ValidationPlaylistSent != false &&
    result?.DeviceResponse == DEVICE_RESPONSE_SHOWING
  ) {
    return {ok: true, text: 'Showing message on rooftop display successfully.'}
  }

  if (result?.DeviceResponse === PAYLOAD_STATUS_CANCELLED) {
    return {
      ok: false,
      text: 'The message was removed on the vehicle before it was shown.',
    }
  }

  /* Reached with no SHOWING confirmation. True because IntegratorService.showValidationPlayList
     (capital L) waits 5 s for the device — two constants, the `validationFuture.get(5, SECONDS)`
     and the matching `handleTimeout` schedule — and, when none answers, sets DeviceOnline = true
     and passes the raw socket status (SENT, RECEIVED, ERROR…) as DeviceResponse. A slow light lands
     here, and its message may still appear. The backend names this file at that catch branch; if
     either side changes the wait or the fallback, the other must follow. */
  if (result?.DeviceOnline === true) {
    return {
      ok: false,
      text: 'The taxi roof light is online but did not confirm showing the message in time. It may still appear.',
    }
  }

  // DeviceOnline absent or null: say neither online nor offline.
  return {ok: false, text: 'The taxi roof light did not confirm showing the message.'}
}
